import { useTranslation } from '../../../lib/i18n';
import type { ConversationActionsFlow } from '../../../lib/use-conversation-actions';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';
import styles from './DeleteConversationConfirm.module.css';

// The confirm in front of deleting a conversation. It takes the same shape as every other destructive
// action here — a named confirm rather than a bare Cancel/OK pair, the destructive skin on the action that
// removes something, and the dismissal spelled out as keeping the chat.
//
// The body says the thread stops being listed rather than promising erasure, because that is what happens:
// the delete deactivates the row and support can still reach it. Telling the user their data is gone when
// it is retained would be the one wrong thing this dialog could say.
//
// Rendered by whichever surface owns the flow; it draws nothing until a target is requested.
export function DeleteConversationConfirm({ flow }: { flow: ConversationActionsFlow }) {
  const { t } = useTranslation();
  if (!flow.target) return null;

  return (
    <Modal
      open
      onClose={flow.dismissDelete}
      width={440}
      maxHeightPx={650}
      scrollBody
      title={t('conversations.delete.title')}
      labelledBy="delete-conversation-title"
      footer={
        <>
          <Button variant="outlined" onClick={flow.dismissDelete} disabled={flow.busy}>
            {t('conversations.delete.keep')}
          </Button>
          <Button color="destructive" loading={flow.busy} onClick={() => void flow.confirmDelete()}>
            {t('conversations.delete.confirm')}
          </Button>
        </>
      }
    >
      <Typography variant="p2" color="secondary" component="p">
        {t('conversations.delete.body', { title: flow.target.title })}
      </Typography>
      {flow.error && (
        <Typography variant="p2" component="p" className={styles.error}>
          {t('conversations.delete.error')}
        </Typography>
      )}
    </Modal>
  );
}
